import {
    CABYS_CODE_LENGTH,
    CABYS_PRODUCT_TYPE_MISMATCH_MESSAGE,
    isCabysCoherentWithProductType,
    isValidCabysCode,
    normalizeCabysCode,
    productTypeFromCabys,
} from "@utils/Cabys";

/** Que se esta auditando: el catalogo de productos o el de categorias. */
export type CabysAuditTarget = "product" | "category";

/**
 * Por que el registro salio en la auditoria.
 *
 * - `invalid`: el CAByS no tiene los 13 digitos de Hacienda.
 * - `missing_type`: nunca se le asigno tipo de producto.
 * - `mismatch`: el CAByS y el tipo se contradicen.
 */
export type CabysAuditStatus = "invalid" | "missing_type" | "mismatch";

export type CabysAuditRow = {
    id: string;
    codigo: string;
    /** `descripcion_fact` en productos, `nombre` en categorias. */
    descripcion: string;
    codigoCabys: string;
    productType: number | null;
    /** El tipo que le tocaria segun el CAByS actual. */
    expectedProductType: number;
    status: CabysAuditStatus;
};

/** Forma minima que necesita la auditoria; la comparten Product y Category. */
export type CabysAuditableItem = {
    id?: string | null;
    codigo?: string | null;
    codigo_cabys?: string | null;
    tipo_producto?: number | null;
    descripcion_fact?: string | null;
    nombre?: string | null;
};

export const CABYS_AUDIT_STATUS_LABELS: Record<CabysAuditStatus, string> = {
    invalid: "CAByS invalido",
    missing_type: "Sin tipo",
    mismatch: "No coincide",
};

export const CABYS_AUDIT_STATUS_MESSAGES: Record<CabysAuditStatus, string> = {
    invalid: `El CAByS debe tener ${CABYS_CODE_LENGTH} digitos.`,
    missing_type: "El registro no tiene tipo de producto asignado.",
    mismatch: CABYS_PRODUCT_TYPE_MISMATCH_MESSAGE,
};

const describe = (item: CabysAuditableItem, target: CabysAuditTarget): string => {
    const raw = target === "category" ? item.nombre : item.descripcion_fact;
    return `${raw ?? ""}`.trim();
};

const auditStatus = (codigoCabys: string, productType: number | null): CabysAuditStatus | null => {
    if (!isValidCabysCode(codigoCabys)) return "invalid";
    if (!productType) return "missing_type";
    if (!isCabysCoherentWithProductType(codigoCabys, productType)) return "mismatch";
    return null;
};

/**
 * Filas de la auditoria: solo los registros incoherentes.
 *
 * Los que ya estan bien no se listan, porque la herramienta existe para corregir y una
 * tabla con miles de filas correctas esconde las pocas que importan.
 */
export const buildCabysAuditRows = (
    items: CabysAuditableItem[],
    target: CabysAuditTarget
): CabysAuditRow[] => {
    const rows: CabysAuditRow[] = [];

    items.forEach((item) => {
        if (!item.id) return;

        const codigoCabys = normalizeCabysCode(item.codigo_cabys);
        const productType = item.tipo_producto ? Number(item.tipo_producto) : null;
        const status = auditStatus(codigoCabys, productType);

        if (!status) return;

        rows.push({
            id: `${item.id}`,
            codigo: `${item.codigo ?? ""}`,
            descripcion: describe(item, target),
            codigoCabys,
            productType,
            expectedProductType: productTypeFromCabys(codigoCabys),
            status,
        });
    });

    return rows;
};

export type CabysFixValidation = { ok: true } | { ok: false; message: string };

/**
 * Valida una correccion antes de escribirla.
 *
 * No basta con que el CAByS sea valido: si la combinacion corregida sigue siendo
 * incoherente el registro volveria a salir en el siguiente escaneo, asi que se rechaza.
 */
export const validateCabysFix = (
    codigoCabys?: string | null,
    productType?: string | number | null
): CabysFixValidation => {
    const cabys = normalizeCabysCode(codigoCabys);

    if (!isValidCabysCode(cabys)) {
        return { ok: false, message: CABYS_AUDIT_STATUS_MESSAGES.invalid };
    }

    if (!Number(productType)) {
        return { ok: false, message: "Debe seleccionar un tipo de producto." };
    }

    if (!isCabysCoherentWithProductType(cabys, productType)) {
        return { ok: false, message: CABYS_PRODUCT_TYPE_MISMATCH_MESSAGE };
    }

    return { ok: true };
};
